import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, merge } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DateMask } from 'src/app/order/masks/date-mask';
import { RneMask } from 'src/app/order/masks/rne-mask';
import { Messages } from 'src/app/order/messages/order.messages';
import { BureauService } from 'src/app/order/services/external/bureau/bureau.service';
import { NationalityService } from 'src/app/order/services/external/nationality/nationality.service';
import { ProposalService } from 'src/app/order/services/external/proposal/proposal.service';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { WizardService } from 'src/app/order/services/internal/wizard/wizard.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { DataStore } from 'src/app/order/store/data.store';
import { PercentValidator } from 'src/app/order/validators/percent.validator';
import { dateValidator } from 'src/app/order/validators/date-validator';
import { FdInputConfig } from 'src/app/shared/fd-form-components/fd-input/fd-input.component';
import { QsaPartnerModel } from 'src/app/shared/models/qsa-partner.model';
import { FdFieldConfigs } from 'src/app/shared/shared-components.module';
import { ConfigurationService } from 'src/app/start/services/configuration.service';
import { FormStep } from '../../form-step';
import { BureauCnpjInformationModel, QsaRow, QsaSourcePartner } from './models/bureau-cnpj-information.model';
import { QsaTreeHost } from './models/qsa-tree-host.model';
import { ValidatePartner, ValidatePartnerRequest } from './models/validate-partner.model';
import { QsaNodeComponent } from './qsa-node/qsa-node.component';

@Component({
  selector: 'app-qsa-data',
  templateUrl: './qsa-data.component.html',
  styleUrls: ['./qsa-data.component.scss'],
  standalone: false,
})
export class QsaDataComponent extends FormStep implements OnInit, OnDestroy, QsaTreeHost {
  public STEP_NUMBER = 0;

  @ViewChild(QsaNodeComponent)
  private tree: QsaNodeComponent;

  public qsaRows: QsaRow[] = [];
  public bureauLoaded = false;
  public bureauError: string;
  public nationalityList = [];

  public readonly PEP_TOOLTIP =
    'Pessoa Exposta Politicamente (PEP), é aquela que desempenha ou desempenhou nos últimos 5 anos, no Brasil ou no exterior, ' +
    'cargo, emprego ou função pública relevante, incluindo políticos (eleitos ou candidatos) e dirigentes de partidos políticos, ' +
    'assim como seus representantes, familiares e outras pessoas de seu relacionamento próximo.';

  // Mesmos nos de qsaRows em ordem linear, alinhados com os indices do FormArray.
  private flatRows: QsaRow[] = [];

  private readonly destroyed$ = new Subject<void>();

  // Os bindings da arvore sao reavaliados a cada ciclo de deteccao; os caches sao limpos a cada mudanca do formulario.
  private participationCache = new Map<QsaRow[], number>();
  private nodeCompleteCache = new Map<QsaRow, boolean>();

  // Campos ja preenchidos que estao exibidos como texto, no formato `${formIndex}:${field}`.
  private confirmedFields = new Set<string>();

  private readonly FIELD_LABELS: { [field: string]: string } = {
    birthDate: 'Data de nascimento',
    openDate: 'Data de fundação',
    nationality: 'Nacionalidade',
    companyParticipation: 'Participação',
  };

  public get qsaFormsControls() {
    return this.qsaFormsArray.controls;
  }

  public getPepControl(node: QsaRow): UntypedFormControl {
    return this.qsaFormsArray.at(node.formIndex).get('politicalExposition') as UntypedFormControl;
  }

  private get qsaFormsArray(): UntypedFormArray {
    return this.formControls.qsaForms as UntypedFormArray;
  }

  private get cnpj(): string {
    return `${this.proposal?.registrationDataPersonalInfo?.cpfCnpj ?? ''}`.replace(/\D/g, '');
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private bureauService: BureauService,
    private nationalityService: NationalityService,
    protected proposalService: ProposalService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected dataStore: DataStore,
    protected scrollService: ScrollService,
    protected wizardService: WizardService,
    protected matDialog: MatDialog,
    protected configurationService: ConfigurationService,
    protected routingService: RoutingService,
    protected dataStoreService: DataStoreService
  ) {
    super(
      dataStore,
      scrollService,
      wizardService,
      matDialog,
      proposalService,
      loadingService,
      dialogService,
      configurationService,
      routingService,
      dataStoreService
    );
  }

  public async ngOnInit() {
    await super.ngOnInit();
    // statusChanges cobre as trocas de validador, que nao emitem valueChanges.
    merge(this.formGroup.valueChanges, this.formGroup.statusChanges)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.clearTreeStateCache());
    this.loadNationalityList();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    super.ngOnDestroy();
  }

  public next(): void {
    this.submitted = true;

    if (this.bureauError || !this.qsaRows.length) {
      this.dialogService.openDialog(Messages.QSA_DATA_UNAVAILABLE);
      return;
    }

    // Campos invalidos podem estar em niveis fechados da arvore.
    if (!this.isFormGroupValid()) {
      this.tree?.openAll();
      return;
    }

    if (!this.isLevelComplete(this.qsaRows)) {
      this.tree?.openAll();
      this.dialogService.openDialog(Messages.QSA_PARTICIPATION_ERROR);
      return;
    }

    // No PFAC o quadro societario pode ser editado na tela, entao o bureau nao valida a composicao.
    if (this.isPfacAccreditationType()) {
      super.next();
      return;
    }

    this.validatePartner();
  }

  private buildQsaTree(nodes: QsaRow[]): QsaPartnerModel[] {
    return nodes.map((node) => {
      const qsaNode = this.buildQsaTreeNode(node);

      if (node.legalRepresentative) {
        qsaNode.representanteLegal = this.buildQsaTreeNode(node.legalRepresentative);
      }

      if (node.children.length) {
        qsaNode.socios = this.buildQsaTree(node.children);
      }

      return qsaNode;
    });
  }

  private buildQsaTreeNode(node: QsaRow): QsaPartnerModel {
    const value = this.qsaFormsArray.at(node.formIndex).getRawValue();
    const isIndividual = node.documentType === 'CPF';
    const qsaNode: QsaPartnerModel = { nome: node.name };

    if (isIndividual) {
      qsaNode.cpf = node.document;
      qsaNode.birthDate = value.birthDate;
      qsaNode.nationality = value.nationality;
      qsaNode.rne = value.rne;
    } else {
      qsaNode.cnpj = node.document;
      qsaNode.openDate = value.openDate;
    }

    if (!node.isLegalRepresentative) {
      qsaNode.companyParticipation = value.companyParticipation;
    }

    if (isIndividual) {
      qsaNode.politicalExposition = value.politicalExposition;
    }

    return qsaNode;
  }

  private validatePartner(): void {
    this.loadingService.adicionaLoadingEvent('validatePartner');

    this.bureauService
      .validatePartner(this.buildValidatePartnerRequest())
      .pipe(finalize(() => this.loadingService.removeLoadingEvent('validatePartner')))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (valid) => {
          if (valid === true) {
            super.next();
            return;
          }

          this.dialogService.openDialog(Messages.QSA_VALIDATION_ERROR);
        },
        // Erro tecnico do Bureau (500/timeout), diferente da reprovacao de negocio (200 + false).
        () => this.dialogService.openConfirmDialog(Messages.QSA_VALIDATION_UNAVAILABLE, () => this.validatePartner())
      );
  }

  private buildValidatePartnerRequest(): ValidatePartnerRequest {
    return {
      cnpj: this.cnpj,
      socios: this.buildValidatePartners(this.qsaRows),
    };
  }

  private buildValidatePartners(nodes: QsaRow[]): ValidatePartner[] {
    return nodes.map((node) => {
      const value = this.qsaFormsArray.at(node.formIndex).getRawValue();
      const isIndividual = node.documentType === 'CPF';

      const partner: ValidatePartner = {
        porcentagemParticipacao: Number(value.companyParticipation) || 0,
        nome: node.name,
        nacionalidade: value.nationality || null,
        rne: value.rne || null,
        isPep: !!value.politicalExposition,
        representanteLegal: node.legalRepresentative
          ? { cpf: node.legalRepresentative.document, nome: node.legalRepresentative.name }
          : null,
      };

      if (isIndividual) {
        partner.cpf = node.document;
        partner.dataNascimento = this.toIsoDate(value.birthDate);
      } else {
        partner.cnpj = node.document;
        partner.dataFundacao = this.toIsoDate(value.openDate);
      }

      if (node.children.length) {
        partner.socios = this.buildValidatePartners(node.children);
      }

      return partner;
    });
  }

  private toIsoDate(value: string): string {
    const digits = `${value ?? ''}`.replace(/\D/g, '');
    return digits.length === 8 ? `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}` : null;
  }

  // A lista precisa estar disponivel antes de renderizar as linhas, pois fd-nationality le os itens no proprio ngOnInit.
  private loadNationalityList(): void {
    this.nationalityService
      .getNationalityList()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response) => {
          this.nationalityList = this.withBrazilianFirst(response ?? []);
          this.loadCnpjInformation();
        },
        () => {
          this.nationalityList = [];
          this.loadCnpjInformation();
        }
      );
  }

  private withBrazilianFirst(list: any[]): any[] {
    const index = list.findIndex((item) => item?.value === 'Brasileiro');

    return index > 0 ? [list[index], ...list.filter((_, position) => position !== index)] : list;
  }

  public loadCnpjInformation(): void {
    this.bureauService
      .getCnpjInformation(this.cnpj)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response) => {
          const saved = this.proposal?.qsa?.length ? this.toBureauCnpjInformation(this.proposal.qsa) : null;
          // Os valores salvos so podem ser reaproveitados se o quadro societario continuar identico ao do bureau.
          const reuseSaved = !!saved && this.hasSameDocuments(response?.socios, saved.socios);

          if (reuseSaved) {
            this.applyBureauNames(response?.socios, saved.socios);
          }

          this.qsaRows = this.buildQsaRows(reuseSaved ? saved : response);
          this.buildQsaForms(this.flatRows);

          if (reuseSaved) {
            this.flatRows.forEach((row) => this.applySavedValues(row));
          }

          this.bureauLoaded = true;
          this.bureauError = null;
        },
        (error) => {
          this.qsaRows = [];
          this.flatRows = [];
          this.bureauLoaded = true;
          this.bureauError = `Falha ao consultar o bureau (status ${error?.status ?? 'desconhecido'}).`;
        }
      );
  }

  private hasSameDocuments(bureauPartners: QsaSourcePartner[], savedPartners: QsaSourcePartner[]): boolean {
    const bureau = bureauPartners ?? [];
    const saved = savedPartners ?? [];

    if (bureau.length !== saved.length) {
      return false;
    }

    return bureau.every((partner) => {
      const match = saved.find((item) => this.documentOf(item) === this.documentOf(partner));

      return (
        !!match &&
        this.documentOf(match.representanteLegal) === this.documentOf(partner.representanteLegal) &&
        this.hasSameDocuments(partner.socios, match.socios)
      );
    });
  }

  private documentOf(partner: QsaSourcePartner): string {
    // O bureau pode devolver o documento formatado; a comparacao usa somente digitos.
    return `${partner?.cnpj ?? partner?.cpf ?? partner?.document ?? ''}`.replace(/\D/g, '');
  }

  // O nome vigente e sempre o do bureau; o salvo na proposta pode estar desatualizado.
  private applyBureauNames(bureauPartners: QsaSourcePartner[], savedPartners: QsaSourcePartner[]): void {
    (bureauPartners ?? []).forEach((partner) => {
      const match = (savedPartners ?? []).find((item) => this.documentOf(item) === this.documentOf(partner));

      if (!match) {
        return;
      }

      match.nome = partner?.nome;

      if (match.representanteLegal && partner?.representanteLegal) {
        match.representanteLegal.nome = partner.representanteLegal.nome;
      }

      this.applyBureauNames(partner?.socios, match.socios);
    });
  }

  public buildQsaRows(response: BureauCnpjInformationModel): QsaRow[] {
    this.flatRows = [];
    return this.buildQsaNodes(response?.socios, '', 0);
  }

  // A proposta guarda QsaPartnerModel; a tela trabalha com o contrato do bureau.
  private toBureauCnpjInformation(qsa: QsaPartnerModel[]): BureauCnpjInformationModel {
    return { socios: this.toSourcePartners(qsa) };
  }

  private toSourcePartners(partners: QsaPartnerModel[]): QsaSourcePartner[] {
    return (partners ?? []).map((partner) => this.toSourcePartner(partner));
  }

  private toSourcePartner(partner: QsaPartnerModel): QsaSourcePartner {
    return {
      nome: partner?.nome,
      cpf: partner?.cpf,
      cnpj: partner?.cnpj,
      document: partner?.cpf ?? partner?.cnpj,
      birthDate: partner?.birthDate,
      openDate: partner?.openDate,
      nationality: partner?.nationality,
      rne: partner?.rne,
      companyParticipation: partner?.companyParticipation,
      politicalExposition: !!partner?.politicalExposition,
      pep: !!partner?.politicalExposition,
      representanteLegal: partner?.representanteLegal ? this.toSourcePartner(partner.representanteLegal) : null,
      socios: partner?.socios?.length ? this.toSourcePartners(partner.socios) : [],
    };
  }

  private applySavedValues(row: QsaRow): void {
    const saved = row.savedData;

    if (!saved) {
      return;
    }

    this.qsaFormsArray.at(row.formIndex).patchValue({
      birthDate: saved.birthDate ?? '',
      openDate: saved.openDate ?? '',
      nationality: saved.nationality ?? '',
      rne: saved.rne ?? '',
      companyParticipation: saved.companyParticipation ?? '',
      politicalExposition: !!saved.politicalExposition,
    });

    this.applyRneState(row);
    this.collapseFilledFields(row);
  }

  // fd-nationality so instala o required do RNE na interacao do usuario; valores restaurados precisam disso aqui.
  private applyRneState(row: QsaRow): void {
    const group = this.qsaFormsArray.at(row.formIndex);
    const nationality = group.get('nationality').value;
    const isForeign = !!nationality && nationality !== 'Brasileiro';
    const rne = group.get('rne');

    if (isForeign) {
      rne.setValidators([Validators.required]);
    } else {
      rne.clearValidators();
    }

    rne.updateValueAndValidity();
    group.get('rneEnabled').setValue(isForeign);
    // fd-nationality decide a exibicao do RNE no ngOnInit a partir de rneEnabled.disabled.
    (this.fieldsArray[row.formIndex].rneEnabled as FdInputConfig).disabled = !isForeign;
  }

  private collapseFilledFields(row: QsaRow): void {
    const isIndividual = row.documentType === 'CPF';
    const fields = isIndividual ? ['birthDate', 'nationality'] : ['openDate'];

    if (!row.isLegalRepresentative) {
      fields.push('companyParticipation');
    }

    fields.filter((field) => this.isFieldComplete(row, field)).forEach((field) => this.confirmedFields.add(this.fieldKey(row, field)));
  }

  public getNodeTitle(node: QsaRow): string {
    if (node.isLegalRepresentative) {
      return 'Representante Legal';
    }

    const title = node.documentType === 'CNPJ' ? 'Dados do Sócio Pessoa Jurídica' : 'Dados do Sócio Pessoa Física';

    return `${node.prefix} ${title}`;
  }

  public isCollapsed(node: QsaRow, field: string): boolean {
    return this.confirmedFields.has(this.fieldKey(node, field));
  }

  public editField(node: QsaRow, field: string): void {
    if (field === 'nationality') {
      this.applyRneState(node);
    }

    this.confirmedFields.delete(this.fieldKey(node, field));
  }

  public confirmField(node: QsaRow, field: string, event: FocusEvent): void {
    const nextFocus = event?.relatedTarget as HTMLElement;
    const wrapper = event?.currentTarget as HTMLElement;

    // O painel do select vive no cdk-overlay-container, fora do wrapper, e nao deve encerrar a edicao.
    if (nextFocus && (wrapper?.contains(nextFocus) || nextFocus.closest('.cdk-overlay-container'))) {
      return;
    }

    if (this.isFieldComplete(node, field)) {
      this.confirmedFields.add(this.fieldKey(node, field));
    }
  }

  public getFieldLabel(field: string): string {
    return this.FIELD_LABELS[field];
  }

  public getTotalParticipation(nodes: QsaRow[]): string {
    return `${this.getTotalParticipationValue(nodes).toFixed(2).replace('.', ',')} %`;
  }

  public isLevelComplete(nodes: QsaRow[]): boolean {
    return this.getTotalParticipationValue(nodes) === 100 && nodes.every((node) => this.isNodeComplete(node));
  }

  public isNodeComplete(node: QsaRow): boolean {
    if (this.nodeCompleteCache.has(node)) {
      return this.nodeCompleteCache.get(node);
    }

    const complete = this.computeNodeComplete(node);
    this.nodeCompleteCache.set(node, complete);

    return complete;
  }

  private computeNodeComplete(node: QsaRow): boolean {
    if (!this.qsaFormsArray.at(node.formIndex).valid) {
      return false;
    }

    if (node.legalRepresentative && !this.qsaFormsArray.at(node.legalRepresentative.formIndex).valid) {
      return false;
    }

    return !node.children.length || this.isLevelComplete(node.children);
  }

  private clearTreeStateCache(): void {
    this.participationCache.clear();
    this.nodeCompleteCache.clear();
  }

  public isPfacAccreditationType(): boolean {
    return this.proposal?.registrationDataPersonalInfo?.isPfac === true;
  }

  public canRemoveNode(node: QsaRow): boolean {
    return this.isPfacAccreditationType() && node.level === 0 && this.qsaRows.length > 1;
  }

  public removeNode(node: QsaRow): void {
    const removedRows = this.collectRows(node);
    // As chaves de confirmedFields usam formIndex, que muda apos a reindexacao.
    const confirmed = Array.from(this.confirmedFields).map((key) => {
      const [index, field] = key.split(':');
      return { row: this.flatRows[Number(index)], field };
    });

    removedRows
      .map((row) => row.formIndex)
      .sort((first, second) => second - first)
      .forEach((index) => {
        this.qsaFormsArray.removeAt(index);
        this.fieldsArray.splice(index, 1);
      });

    this.flatRows = this.flatRows.filter((row) => !removedRows.includes(row));
    this.flatRows.forEach((row, index) => (row.formIndex = index));
    this.qsaRows = this.qsaRows.filter((row) => row !== node);

    this.confirmedFields.clear();
    confirmed
      .filter((item) => item.row && !removedRows.includes(item.row))
      .forEach((item) => this.confirmedFields.add(this.fieldKey(item.row, item.field)));

    this.clearTreeStateCache();
  }

  private collectRows(node: QsaRow): QsaRow[] {
    const rows = [node, ...(node.legalRepresentative ? [node.legalRepresentative] : [])];

    return node.children.reduce((all, child) => [...all, ...this.collectRows(child)], rows);
  }

  private getTotalParticipationValue(nodes: QsaRow[]): number {
    if (this.participationCache.has(nodes)) {
      return this.participationCache.get(nodes);
    }

    const total = nodes
      .filter((node) => !node.isLegalRepresentative)
      .reduce((sum, node) => sum + (Number(this.qsaFormsArray.at(node.formIndex).get('companyParticipation').value) || 0), 0);

    // Sem o arredondamento a soma de decimais nao fecha exatamente em 100.
    const rounded = Math.round(total * 100) / 100;
    this.participationCache.set(nodes, rounded);

    return rounded;
  }

  public getFieldValue(node: QsaRow, field: string): string {
    const group = this.qsaFormsArray.at(node.formIndex);
    const value = group.get(field).value;

    if (field === 'nationality') {
      const rne = group.get('rne').value;
      return rne ? `${value} - RNE ${rne}` : value;
    }

    if (field === 'companyParticipation') {
      return `${(Number(value) || 0).toFixed(2).replace('.', ',')} %`;
    }

    return this.formatDate(value);
  }

  private isFieldComplete(node: QsaRow, field: string): boolean {
    const group = this.qsaFormsArray.at(node.formIndex);
    const control = group.get(field);

    if (!control.value || control.invalid) {
      return false;
    }

    return field === 'nationality' ? group.get('rne').valid : true;
  }

  private fieldKey(node: QsaRow, field: string): string {
    return `${node.formIndex}:${field}`;
  }

  private formatDate(value: string): string {
    const digits = `${value ?? ''}`.replace(/\D/g, '');
    return digits.length === 8 ? `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}` : value;
  }

  private buildQsaNodes(partners: QsaSourcePartner[], parentPrefix: string, level: number): QsaRow[] {
    if (!partners?.length) {
      return [];
    }

    return partners.reduce<QsaRow[]>((nodes, partner, index) => {
      const prefix = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;

      const node = this.createRow({
        // O nivel raiz e o unico numerado com ponto final, conforme o layout pedido.
        prefix: level === 0 ? `${prefix}.` : prefix,
        name: partner?.nome,
        documentType: partner?.cnpj ? 'CNPJ' : 'CPF',
        document: partner?.cnpj ?? partner?.cpf ?? partner?.document,
        level,
        isLegalRepresentative: false,
        politicalExposition: !!(partner?.politicalExposition ?? partner?.pep),
        savedData: partner,
      });

      node.children = this.buildQsaNodes(partner?.socios, prefix, level + 1);
      // O representante legal e exibido dentro do painel do proprio socio.
      node.legalRepresentative = this.createLegalRepresentativeRow(partner, prefix, level);
      nodes.push(node);

      return nodes;
    }, []);
  }

  private createLegalRepresentativeRow(partner: QsaSourcePartner, prefix: string, level: number): QsaRow {
    const legalRepresentative = partner?.representanteLegal;

    if (!legalRepresentative?.cpf) {
      return null;
    }

    return this.createRow({
      prefix: `${prefix}.R`,
      name: legalRepresentative.nome,
      documentType: 'CPF',
      document: legalRepresentative.cpf,
      level,
      isLegalRepresentative: true,
      politicalExposition: !!(legalRepresentative.politicalExposition ?? legalRepresentative.pep),
      savedData: legalRepresentative,
    });
  }

  private createRow(row: Omit<QsaRow, 'formIndex' | 'children'>): QsaRow {
    // O bureau pode devolver o documento ja formatado; o pipe cpfCnpj exige somente digitos.
    const document = `${row.document ?? ''}`.replace(/\D/g, '');
    const node: QsaRow = { ...row, document, formIndex: this.flatRows.length, children: [] };
    this.flatRows.push(node);
    return node;
  }

  private buildQsaForms(rows: QsaRow[]): void {
    this.qsaFormsArray.clear();
    this.fieldsArray = [];
    this.confirmedFields.clear();

    rows.forEach((row) => {
      const isIndividual = row.documentType === 'CPF';
      const hasParticipation = !row.isLegalRepresentative;
      this.fieldsArray.push(this.createFields());
      this.qsaFormsArray.push(
        this.formBuilder.group({
          birthDate: ['', isIndividual ? [Validators.required, dateValidator()] : []],
          openDate: ['', isIndividual ? [] : [Validators.required, dateValidator()]],
          // fd-currency-input invoca control.validator sem checar nulo, entao o controle precisa ter validador.
          companyParticipation: [
            '',
            hasParticipation ? [Validators.required, PercentValidator(0, 100, false)] : [PercentValidator(0, 100, false)],
          ],
          nationality: ['', isIndividual ? [Validators.required] : []],
          // fd-nationality aplica/remove o required do RNE conforme a nacionalidade escolhida.
          rne: [''],
          rneEnabled: [false],
          politicalExposition: [row.politicalExposition],
        })
      );
    });
  }

  protected createFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      qsaForms: this.formBuilder.array([]),
    });
  }

  protected createFields(): FdFieldConfigs {
    return {
      birthDate: {
        label: 'Data de nascimento',
        controlName: 'birthDate',
        mask: DateMask,
        maskCharsReplace: /[.\/ -]/g,
        messages: {
          required: 'Informe a data de nascimento',
          invalid: 'Data inválida',
        },
      },
      openDate: {
        label: 'Data de fundação',
        controlName: 'openDate',
        mask: DateMask,
        maskCharsReplace: /[/\//]/g,
        messages: {
          required: 'Informe a data de fundação',
          invalid: 'Data inválida',
        },
      },
      companyParticipation: {
        label: 'Percentual de participação',
        controlName: 'companyParticipation',
        customOptions: { prefix: '', suffix: ' %', thousands: '.', decimal: ',', align: 'left', min: 0, max: 100 },
        messages: {
          required: 'Informe a participação na empresa',
          valueZero: 'Informe a participação na empresa',
          notNumeric: 'Informe um valor numérico',
          valueTooLow: 'A participação não pode ser negativa',
          valueTooHigh: 'A participação não pode passar de 100%',
        },
      },
      nationality: {
        label: 'Nacionalidade',
        items: [],
        controlName: 'nationality',
        searchPlaceholder: 'Buscar',
        searchable: true,
        searchInputFirst: true,
        searchInputCleanOnClose: true,
        lettersOnly: true,
        required: true,
        messages: {
          required: 'Informe a nacionalidade',
          invalid: 'Nacionalidade inválida',
          pattern: 'Nacionalidade inválida',
        },
      },
      rne: {
        label: 'Registro Nacional de Estrangeiro (RNE)',
        controlName: 'rne',
        mask: RneMask,
        maskCharsReplace: /[-]/g,
        messages: {
          required: 'Informe o RNE',
        },
      },
      rneEnabled: {
        controlName: 'rneEnabled',
        disabled: true,
      },
    };
  }

  protected updateFields(): void {}

  protected proposalGetCallback(): void {}

  protected persistData(): Promise<boolean> {
    const qsa = this.buildQsaTree(this.qsaRows);
    return this.dataStore.updateProposal({ qsa });
  }
}
